/**
 * Kundenstimmen auf der öffentlichen Seite.
 *
 * Ein Karussell ist ein Bedienelement, das gern schlecht gebaut wird. Vier
 * Dinge sind deshalb keine Kür:
 *
 *  1. Es lässt sich anhalten. Text, der von selbst weiterläuft und länger
 *     als fünf Sekunden dauert, braucht einen Halteschalter (WCAG 2.2.2) –
 *     wer langsam liest, verliert sonst mitten im Satz den Satz.
 *  2. Es hält von selbst an, solange die Maus darüber steht, ein Knopf
 *     darin den Fokus hat oder das Fenster im Hintergrund liegt.
 *  3. Es fährt gar nicht erst los, wenn „Bewegung reduzieren" eingestellt
 *     ist. Dann wird auch nicht geschoben, sondern gewechselt.
 *  4. Es ist mit der Tastatur bedienbar – Pfeil links und rechts – und für
 *     die Sprachausgabe als Karussell ausgezeichnet.
 *
 * Bei einer einzigen Stimme entfällt die ganze Mechanik: dann steht sie
 * einfach da. Bei keiner erscheint der Abschnitt nicht.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useSparsameBewegung } from './Bewegung'

export type Referenz = {
  einrichtung: string
  ort: string | null
  text: string
  person: string | null
  funktion: string | null
}

/** Wie lange eine Stimme stehen bleibt. Langsam genug zum Lesen. */
const STANDZEIT = 7000

export function Referenzkarussell({ referenzen }: { referenzen: Referenz[] }) {
  const sparsam = useSparsameBewegung()
  const [jetzt, setzeJetzt] = useState(0)
  const [angehalten, setzeAngehalten] = useState(false)
  // Getrennt vom Halteschalter: das hier ist die vorübergehende Pause,
  // solange die Maus darüber steht. Sonst würde ein Mausschwenk den
  // Schalter umlegen, den der Besucher gedrückt hat.
  const [ruht, setzeRuht] = useState(false)
  const bereich = useRef<HTMLDivElement>(null)
  const kennung = useId()
  // Wischen auf dem Handy. Bewusst schlicht: nur die waagerechte Strecke
  // zwischen Auflegen und Abheben zählt, damit ein senkrechter Scrollzug
  // nicht versehentlich weiterblättert.
  const start = useRef<{ x: number; y: number } | null>(null)

  const anzahl = referenzen.length
  const mehrere = anzahl > 1

  const gehZu = useCallback(
    (ziel: number) => setzeJetzt(((ziel % anzahl) + anzahl) % anzahl),
    [anzahl],
  )

  // Selbsttätiges Weiterblättern. Läuft nicht, wenn es nur eine Stimme gibt,
  // wenn angehalten wurde, wenn die Maus darüber steht – und nie bei
  // „Bewegung reduzieren".
  useEffect(() => {
    if (!mehrere || sparsam || angehalten || ruht) return
    const uhr = window.setInterval(() => setzeJetzt((i) => (i + 1) % anzahl), STANDZEIT)
    return () => window.clearInterval(uhr)
  }, [mehrere, sparsam, angehalten, ruht, anzahl])

  // Im Hintergrundfenster nicht weiterlaufen. Sonst kommt der Besucher
  // zurück und die Stimme, bei der er war, ist längst durchgelaufen.
  useEffect(() => {
    if (!mehrere) return
    const wechsel = () => setzeRuht(document.hidden)
    document.addEventListener('visibilitychange', wechsel)
    return () => document.removeEventListener('visibilitychange', wechsel)
  }, [mehrere])

  if (anzahl === 0) return null

  function taste(ereignis: React.KeyboardEvent) {
    if (!mehrere) return
    if (ereignis.key === 'ArrowRight') {
      ereignis.preventDefault()
      gehZu(jetzt + 1)
    } else if (ereignis.key === 'ArrowLeft') {
      ereignis.preventDefault()
      gehZu(jetzt - 1)
    }
  }

  return (
    <section
      className="oe-abschnitt"
      aria-labelledby={`${kennung}-titel`}
      onMouseEnter={() => setzeRuht(true)}
      onMouseLeave={() => setzeRuht(false)}
    >
      {/* „Referenzen" und nicht „Was Auftraggeber sagen": dieselben Einträge
          stehen auch auf spuer.net, und dort sind es keine Zitate, sondern
          Auftraggeber mit dem, was für sie geprüft wurde. Eine Überschrift,
          die ein Zitat verspricht, wäre für die Hälfte der Einträge falsch. */}
      <h2 id={`${kennung}-titel`} className="oe-titel">
        Referenzen
      </h2>

      <div
        ref={bereich}
        className="oe-karussell"
        role="group"
        aria-roledescription="Karussell"
        aria-label="Kundenstimmen"
        tabIndex={mehrere ? 0 : -1}
        onKeyDown={taste}
        onFocus={() => setzeRuht(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setzeRuht(false)
        }}
        onPointerDown={(e) => {
          start.current = { x: e.clientX, y: e.clientY }
        }}
        onPointerUp={(e) => {
          const anfang = start.current
          start.current = null
          if (!anfang || !mehrere) return
          const dx = e.clientX - anfang.x
          const dy = e.clientY - anfang.y
          if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return
          gehZu(jetzt + (dx < 0 ? 1 : -1))
        }}
      >
        <div
          className="oe-karussell-bahn"
          style={{
            transform: `translateX(-${jetzt * 100}%)`,
            transition: sparsam ? 'none' : undefined,
          }}
        >
          {referenzen.map((referenz, i) => (
            <figure
              key={`${referenz.einrichtung}-${i}`}
              className="oe-stimme"
              role="group"
              aria-roledescription="Stimme"
              aria-label={`${i + 1} von ${anzahl}`}
              // Was nicht zu sehen ist, gehört nicht in den Tabulatorlauf
              // und nicht in die Vorlesereihenfolge.
              aria-hidden={i !== jetzt}
              inert={i !== jetzt}
            >
              <blockquote className="oe-stimme-text">{referenz.text}</blockquote>
              <figcaption className="oe-stimme-quelle">
                <span className="oe-stimme-haus">
                  {referenz.einrichtung}
                  {referenz.ort ? `, ${referenz.ort}` : ''}
                </span>
                {(referenz.person || referenz.funktion) && (
                  <span className="oe-stimme-person">
                    {[referenz.person, referenz.funktion].filter(Boolean).join(' · ')}
                  </span>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      {mehrere && (
        <div className="oe-karussell-leiste">
          <button
            type="button"
            className="oe-rundknopf"
            onClick={() => gehZu(jetzt - 1)}
            aria-label="Vorherige Stimme"
          >
            <Pfeil richtung="links" />
          </button>

          <div className="oe-punkte" role="tablist" aria-label="Stimme wählen">
            {referenzen.map((referenz, i) => (
              <button
                key={`${referenz.einrichtung}-${i}`}
                type="button"
                role="tab"
                aria-selected={i === jetzt}
                aria-label={`Stimme ${i + 1} von ${anzahl}: ${referenz.einrichtung}`}
                className="oe-punkt"
                onClick={() => gehZu(i)}
              >
                <span aria-hidden="true" />
              </button>
            ))}
          </div>

          <button
            type="button"
            className="oe-rundknopf"
            onClick={() => gehZu(jetzt + 1)}
            aria-label="Nächste Stimme"
          >
            <Pfeil richtung="rechts" />
          </button>

          {/* Der Halteschalter entfällt bei „Bewegung reduzieren": dort
              läuft ohnehin nichts, was man anhalten müsste. */}
          {!sparsam && (
            <button
              type="button"
              className="oe-halteknopf"
              onClick={() => setzeAngehalten((a) => !a)}
              aria-pressed={angehalten}
            >
              {angehalten ? 'Weiter' : 'Anhalten'}
            </button>
          )}
        </div>
      )}
    </section>
  )
}

function Pfeil({ richtung }: { richtung: 'links' | 'rechts' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={richtung === 'links' ? 'M15 5 8 12l7 7' : 'M9 5l7 7-7 7'} />
    </svg>
  )
}
